/**
 * useStageFit — measure the slot, hand back the correctly-shaped stage
 * (2026-09-04).
 *
 * WHY MEASURED JS AND NOT `aspect-ratio`
 * --------------------------------------
 * `aspect-ratio` applies only while ONE axis is automatic, and a min/max on
 * the automatic axis is never transferred back out to the definite one. So
 * both of the tempting declarations fit in exactly one direction:
 *
 *     width: 100%;  aspect-ratio: 16/10; max-height: 100%;  // squats
 *     height: 100%; aspect-ratio: 16/10; max-width: 100%;   // squats
 *
 * The clamped axis shrinks and the other one does not follow, so the ratio is
 * quietly abandoned. Our slot is width-bound on some viewports (1366x768,
 * phone portrait) and height-bound on others (anything 16:9), so a
 * one-direction fit would guarantee black bars on half the fleet — the exact
 * defect being fixed.
 *
 * `min()`/`dvh` is a known trap in this codebase besides: PanelModal records
 * that a `min(86vh, 92dvh)` drops the WHOLE declaration where dvh is
 * unsupported, leaving no cap at all.
 *
 * The reliable pure-CSS both-directions fit needs a replaced element with
 * intrinsic dimensions (an SVG sizer) and some spec-corner plumbing to hang
 * the iframe on. Reliable mechanism, unreliable plumbing — and a degradation
 * there shows up as the very bars we are removing.
 *
 * So: measure. It is the idiom this repo already trusts for the /desktop
 * page's one-screen stage (measure, clamp, explicit px, resize +
 * orientationchange + ResizeObserver + settle timers + a sub-pixel
 * dead-band), and it makes the scale cap, the fit and the fullscreen budget
 * one arithmetic expression instead of three CSS corners.
 *
 * WHY THE OBSERVER CANNOT FEED ITSELF
 * -----------------------------------
 * It observes the SLOT and writes only the STAGE's pixel size. The slot is
 * `flex: 1` (basis 0) inside a card with a DEFINITE height, so its used size
 * is `card − chrome` by width and height alike: neither term contains the
 * stage. The stage is `flex: none` at explicit px inside a slot with
 * `overflow: hidden`, so an over-large stage cannot push anything either.
 * The observed quantity is provably independent of the written one.
 *
 * The formulations that DO loop, avoided deliberately: observing the card or
 * the body while the card is shrink-to-fit (the stage's height is then part
 * of what is being measured), and deriving the card's WIDTH from the fitted
 * stage (card width → footer wrapping → chrome height → slot height → stage
 * height → stage width → card width oscillates at the wrap boundary). The
 * card's size is a function of the viewport alone for that reason.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { fitRemoteStage, type StageFit } from './stageFit';

/**
 * Ignore sub-pixel churn. Borrowed from the /desktop page's stage measure: a
 * resize storm otherwise re-renders on every fractional rect change.
 */
const DEAD_BAND_PX = 2;

/** Settle passes after the first measure — see the comment on `measure`. */
const SETTLE_MS = [120, 600] as const;

/** Safari does not reliably fire `resize` across a fullscreen transition. */
const FULLSCREEN_SETTLE_MS = 350;

export interface UseStageFit {
  /** Attach to the slot — the box the stage is fitted INSIDE. */
  slotRef: React.RefObject<HTMLDivElement>;
  /** Null until the first measure lands (one layout tick, before paint). */
  fit: StageFit | null;
  /** Force a re-measure; used after a fullscreen change. */
  remeasure: () => void;
}

export function useStageFit(): UseStageFit {
  const slotRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<StageFit | null>(null);
  // Read inside `measure` without making it a dependency — `measure` must
  // keep a stable identity or every listener re-subscribes on each fit.
  const fitRef = useRef<StageFit | null>(null);

  const measure = useCallback(() => {
    const el = slotRef.current;
    if (!el) return;
    // THE CONTENT BOX, NOT THE BORDER BOX. The slot carries a 1px border, so
    // getBoundingClientRect() is 2px larger than the space a child can
    // occupy. Fitting to that produces a stage 2px too tall, which the
    // stage's own `maxHeight: 100%` then clamps — breaking the aspect by
    // ~3px and putting a thin black band back exactly where this whole
    // change removes it. Found by the geometry gate, which is the only
    // thing that could have found it.
    let slotW = 0;
    let slotH = 0;
    try {
      slotW = el.clientWidth;
      slotH = el.clientHeight;
    } catch {
      return;
    }
    const next = fitRemoteStage(slotW, slotH);
    if (next.width <= 0 || next.height <= 0) return;
    const prev = fitRef.current;
    if (
      prev
      && Math.abs(prev.width - next.width) < DEAD_BAND_PX
      && Math.abs(prev.height - next.height) < DEAD_BAND_PX
    ) {
      return;
    }
    fitRef.current = next;
    setFit(next);
  }, []);

  // Before paint, so the first frame the user sees is already fitted — no
  // flash of an unfitted stage and no CSS fallback needed for the gap.
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    // Settle passes: the modal's chrome height changes AFTER mount as the
    // access chips, the workspace toggle and a late-arriving custom slug all
    // resolve, and each of those moves the slot. The ResizeObserver below
    // catches them; these are belt for environments without one.
    const timers = SETTLE_MS.map((ms) => window.setTimeout(measure, ms));

    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('orientationchange', measure);

    let observer: ResizeObserver | null = null;
    try {
      if (typeof ResizeObserver !== 'undefined' && slotRef.current) {
        observer = new ResizeObserver(measure);
        observer.observe(slotRef.current);
      }
    } catch {
      /* no ResizeObserver — the listeners and timers above still cover it */
    }

    const onFullscreenChange = () => {
      measure();
      window.setTimeout(measure, FULLSCREEN_SETTLE_MS);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange as EventListener);

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      try {
        observer?.disconnect();
      } catch {
        /* already gone */
      }
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange as EventListener);
    };
  }, [measure]);

  return { slotRef, fit, remeasure: measure };
}

export default useStageFit;
